import React from 'react';
import styled from 'styled-components';
import { SocialMedia, ScrollToTopButton } from 'components';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { AnimatePresence } from 'framer-motion';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useLocation } from '@reach/router';
import { isMobileOnly } from 'react-device-detect';
import AgencyXLogo from 'images/shared/AgencyXLogo';
import { EmailIcon, MobileIcon, LocationIcon } from 'images/footer';

const Footer = () => {
  const { pathname } = useLocation();

  const { iphaLogo, pmiLogo, nuaBadge } = useStaticQuery(graphql`
    query {
      iphaLogo: file(relativePath: { eq: "footer/IphaAffliateLogo.png" }) {
        childImageSharp {
          gatsbyImageData(width: 109)
        }
      }
      pmiLogo: file(relativePath: { eq: "footer/PmiLogo.png" }) {
        childImageSharp {
          gatsbyImageData(width: 66)
        }
      }
      nuaBadge: file(relativePath: { eq: "footer/NuaBadge.png" }) {
        childImageSharp {
          gatsbyImageData(width: 100)
        }
      }
    }
  `);

  return (
    <Container pathname={pathname}>
      <Row $columns={3}>
        <FooterSection>
          <GatsbyLink
            pathname={pathname}
            to="/"
            aria-hidden="true"
            tabIndex={-1}
            onClick={(e) => {
              if (pathname !== '/logobook/') {
                e.preventDefault();
              }
            }}>
            <StyledAgencyXLogo />
          </GatsbyLink>
          <h3>
            Intelligent
            <br />
            Healthcare Marketing.
          </h3>
          <DesktopFindUs>
            <h4>Find Us On</h4>
            <SocialMedia />
          </DesktopFindUs>
        </FooterSection>
        <FooterSection>
          <FooterNav aria-label="Our services">
            <h4>
              <Link to="/services/" style={{ color: 'inherit', textDecoration: 'none' }}>
                Services
              </Link>
            </h4>
            <ul>
              <li>
                <Link to="/services/web-development/">Web Development</Link>
              </li>
              <li>
                <Link to="/services/creative-and-design/">Creative &amp; Design</Link>
              </li>
              <li>
                <Link to="/services/healthcare-brand-and-campaign-strategy/">
                  Healthcare Brand &amp; Campaign Strategy
                </Link>
              </li>
              <li>
                <Link to="/services/healthcare-event-management/">Healthcare Event Management</Link>
              </li>
            </ul>
          </FooterNav>
        </FooterSection>
        <FooterSection>
          <ContactDetails>
            <h4>Contact</h4>
            <div>
              <EmailIcon aria-hidden="true" />
              <p>
                <Link
                  style={{ color: '#fff', cursor: 'pointer', textDecoration: 'underline' }}
                  to="/contact/">
                  Email Us
                </Link>
              </p>
              <MobileIcon aria-hidden="true" />
              <p>
                <a
                  href="tel:+35316706122"
                  style={{ color: '#fff' }}
                  aria-label="Call Us at 01 6706122">
                  01 6706122
                </a>
              </p>
              <LocationIcon aria-hidden="true" />
              <p aria-label="Find us at Charlemont Exchange, Bankside, 13 Charlemont Street, Dublin 2, D02 VN88">
                Bankside (Charlemont Exchange),
                <br aria-hidden="true" />
                13 Charlemont Street, Dublin 2,
                <br aria-hidden="true" />
                D02 VN88, Ireland
              </p>
            </div>
          </ContactDetails>
          <Logos>
            <a
              href="https://nuacomms.com/"
              target="_blank"
              rel="noreferrer"
              aria-label="Associated with NUA - Learn More at nuacomms.com">
              <GatsbyImage
                image={nuaBadge.childImageSharp.gatsbyImageData}
                alt="Associated with NUA - Learn More at nuacomms.com"
                aria-hidden="true"
              />
            </a>
            <a
              href="https://www.ipha.ie/about-us/affiliates-programme/"
              target="_blank"
              rel="noreferrer"
              aria-label="Associated with IPHA Affiliate Programme - Learn More at ipha.ie">
              <GatsbyImage
                image={iphaLogo.childImageSharp.gatsbyImageData}
                alt="Associated with IPHA Affiliate Programme - Learn More at ipha.ie"
                aria-hidden="true"
              />
            </a>
            <a
              href="https://thepmi.com/"
              target="_blank"
              rel="noreferrer"
              aria-label="Associated with PMI - Learn More at thepmi.com">
              <GatsbyImage
                image={pmiLogo.childImageSharp.gatsbyImageData}
                alt="Associated with PMI - Learn More at thepmi.com"
                aria-hidden="true"
              />
            </a>
          </Logos>
        </FooterSection>
      </Row>
      <Row>
        <FooterSection>
          <MobileFindUs>
            <h4>Find Us On</h4>
            <SocialMedia />
          </MobileFindUs>
          <SmallPrint>
            © {new Date().getFullYear()} Agency X. All rights reserved.
            <br aria-hidden="true" />
            AgencyX Limited, registered in Ireland, Reg. No. 472825
          </SmallPrint>
          <SmallPrintLinks>
            <Link to="/terms-and-conditions/">Terms And Conditions</Link>
            <Link to="/privacy-policy/">Privacy Policy</Link>
            <a href="/assets/ai-usage-policy.pdf">AI Policy</a>
            <Link to="/cookie-policy/">Cookie Policy</Link>
            <span
              role="button"
              tabIndex={0}
              className="cky-banner-element"
              aria-label="Manage Cookie Preferences">
              Cookie Preferences
            </span>
          </SmallPrintLinks>
        </FooterSection>
      </Row>
      <AnimatePresence>
        {!isMobileOnly && pathname !== '/logobook/' && <ScrollToTopButton />}
      </AnimatePresence>
    </Container>
  );
};

const GatsbyLink = styled(Link)`
  cursor: ${({ pathname }) => (pathname === '/logobook/' ? 'pointer' : 'default')};
  display: inline;
  width: 11rem;
`;

const StyledAgencyXLogo = styled(AgencyXLogo)`
  position: relative;
  z-index: 2;
  padding: 0.2em 0;
  width: 10rem;
  margin: 3rem 0 2rem;

  path {
    fill: white;
    &:nth-child(2) {
      fill: var(--ax-gold);
    }
  }
`;

const Container = styled.footer`
  background-color: var(--ax-black);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: 1 / -1;
  max-width: 100vw;
  padding-bottom: 1.25rem;
  position: relative;
  top: ${({ pathname }) => pathname === '/logobook/' && '-30px'};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 2.5rem;
  width: 100%;
  padding: 0 1.25rem;
  @media screen and (min-width: 48rem) {
    padding: 0 6.25%;
    grid-template-columns: ${({ $columns }) => ($columns === 3 ? '1.4fr 1fr 1.3fr' : '1fr 1fr')};
  }
  @media screen and (min-width: 90rem) {
    padding: 0 9%;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    color: #fff;
    font-size: 2.5rem;
    font-weight: 900;
    letter-spacing: 0.01em;
    line-height: 1.2;
    margin-bottom: 0.4em;
    text-transform: capitalize;
  }
  h4 {
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.0125em;
    text-transform: uppercase;
    margin: 1.9em 0 1.65em;
    @media screen and (min-width: 48rem) {
      margin-top: 0;
    }
  }
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  h4 {
    margin: 1.5em 0;
  }
  div {
    align-items: center;
    display: grid;
    grid-row-gap: 1rem;
    padding: 0;
    grid-template-columns: 2.95rem 1fr;
    grid-template-rows: repeat(2, 1.625rem);
  }
  svg {
    width: 1.5rem;
  }
  p {
    color: #fff;
    font-size: 0.875rem;
    line-height: 1em;
    &:last-of-type {
      position: relative;
      top: 0.55em;
    }
  }
  @media screen and (min-width: 48rem) {
    margin-bottom: 3rem;
    h4 {
      margin: 2.9em 0 1.5em;
    }
  }
`;

const SmallPrint = styled.p`
  color: #fff;
  font-size: 0.75rem !important;
  margin: 0.75rem 0;
`;

const SmallPrintLinks = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 0.75rem !important;
  justify-content: space-between;
  flex-flow: row wrap;
  gap: 1rem;
  width: 100%;

  a,
  span {
    color: #fff;
    cursor: pointer;
    text-decoration: underline !important;
  }

  #ot-sdk-btn,
  button {
    background-color: transparent !important;
    border: none !important;
    color: #fff !important;
    cursor: pointer !important;
    font-size: 12px !important;
    line-height: unset !important;
    padding: 0 !important;
    text-decoration: underline !important;

    &:link,
    &:visited,
    &:hover,
    &:active,
    &:focus {
      background-color: transparent !important;
    }
  }
  @media screen and (min-width: 48rem) {
    max-width: 42em;
  }
`;

/* Both variants are rendered server-side so the social links are always in the
   static HTML (crawlable); the viewport width decides which one is visible.
   This replaces the old RenderWhenReady + isMobileOnly device detection. */
const FindUs = styled.div`
  display: grid !important;
  grid-template-columns: 1fr !important;
  padding-top: 1rem !important;
  width: 100%;
  div {
    grid-column: span 1;
  }
`;

const DesktopFindUs = styled(FindUs)`
  display: none !important;
  @media screen and (min-width: 48rem) {
    display: grid !important;
  }
`;

const MobileFindUs = styled(FindUs)`
  @media screen and (min-width: 48rem) {
    display: none !important;
  }
`;

const Logos = styled.div`
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-auto-flow: column dense;
  gap: 2rem;
  margin-top: 2rem;
  align-items: center;
  max-width: 380px;
`;

const FooterNav = styled.nav`
  /* Align the Services heading with the Contact heading (h4 margin-top: 2.9em). */
  @media screen and (min-width: 48rem) {
    padding-top: 2.9rem;
  }
  ul {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    list-style: none;
    margin: 0 0 1.5rem;
    padding: 0;
  }
  li {
    line-height: 1.2;
  }
  a {
    color: #fff;
    font-size: 0.875rem;
    text-decoration: none;
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;

export default Footer;
